import React, { useState } from "react";
import { FaSave, FaTimes, FaTrash, FaChevronDown, FaChevronUp } from "react-icons/fa";
import LABEL_OVERRIDES from "./labelOverrides";

// Üst-seviye grup başlıklarının Türkçe etiketleri.
// Makinedeki menü adlarıyla birebir aynı tutulur (kullanıcı kuralı): teknisyen
// panelde ve makine ekranında aynı ismi görmeli.
const GROUP_LABELS = {
  "profile.security": "Şifre Ayarları",
  "profile.appearance": "Görüntü Ayarları",
  "profile.settings": "Sistem Ayarları",
  "profile.timeDate": "Tarih ve Saat",
  "general.teaFilterCoffee": "Çay-Filtre Kahve",
  "general.tea": "Çay",
  "general.filterCoffee": "Filtre Kahve",
  "general.turkishCoffee": "Türk Kahvesi",
  custom: "Göz Ayarları",
};

const SUBGROUP_LABELS = {
  teaFilterCoffee: "Çay-Filtre Kahve",
  turkishCoffee: "Türk Kahvesi",
};

const MODE_LABELS = {
  fast: "Hızlı",
  normal: "Normal",
  gourmet: "Gurme",
};

// Cihazdan gelen setting metadata'sini bul — her change için label'i resolve eder
const resolveSettingMeta = (data, change) => {
  if (!data || !change) return null;
  try {
    if (change.group.startsWith("profile.") || change.group.startsWith("general.")) {
      const [root, sub] = change.group.split(".");
      return data?.[root]?.[sub]?.[change.key] || null;
    } else if (change.group === "custom") {
      const pots = data.custom?.pots || [];
      const pot =
        pots.find(
          (p) =>
            p.index === change.potIndex &&
            (!change.type || p.type === change.type),
        ) || pots.find((p) => p.index === change.potIndex);
      if (!pot) return null;
      // Once nested sub-object dene, yoksa flat
      const sub = pot[change.subgroup];
      const isNestedSub =
        sub && typeof sub === "object" && !Array.isArray(sub) && !("type" in sub);
      const container = isNestedSub ? sub : pot;
      if (change.cupId) {
        const cups = Array.isArray(container.cups) ? container.cups : [];
        const cup = cups.find((c) => c.id === change.cupId);
        if (!cup) return null;
        if (change.mode) {
          return cup.modes?.[change.mode]?.[change.key] || null;
        }
        return cup[change.key] || null;
      }
      return container[change.key] || null;
    }
  } catch (_) {}
  return null;
};

const resolveSettingLabel = (data, change) => {
  // Once client-side override mapping — cihaz reload edilmemis olsa bile dogru
  const override = LABEL_OVERRIDES[change?.key];
  if (override) return override;
  return resolveSettingMeta(data, change)?.label || change?.key || "?";
};

const resolveOldValue = (originalData, change) =>
  resolveSettingMeta(originalData, change)?.value;

const buildChangeContext = (change) => {
  // Üst grup başlığını + custom için göz/fincan/mod bilgisini Türkçe döner
  const parts = [];
  parts.push(GROUP_LABELS[change.group] || change.group);
  if (change.group === "custom") {
    parts.push(`${change.potIndex}. göz`);
    if (change.subgroup && SUBGROUP_LABELS[change.subgroup]) {
      parts.push(SUBGROUP_LABELS[change.subgroup]);
    } else if (change.subgroup) {
      parts.push(change.subgroup);
    }
    if (change.cupId) parts.push(change.cupId);
    if (change.mode) parts.push(MODE_LABELS[change.mode] || change.mode);
  }
  return parts.join(" / ");
};

// Bilinen ID'ler icin Turkce label fallback'i
const KNOWN_ID_LABELS = {
  // Türk kahvesi düzenleme kapsamı (general.turkishCoffee.editScope)
  basit: "Basit",
  standart: "Standart",
  gelismis: "Gelişmiş",
  tr: "Türkçe",
  en: "İngilizce",
};

const formatValue = (value, meta) => {
  // Otomatik karıştırma eğrisi: null = varsayılan eğri
  if (meta && meta.type === "AutoMixerCurve") {
    if (value === null || value === undefined) return "Varsayılan eğri";
    if (typeof value === "object") {
      return `Dirsek ${value.kneeTemp}°C · Plato ${value.plateauHw} · Dirsek hızı ${value.stepHw} · Basamak ${value.stepDown} · Taban ${value.floorHw}`;
    }
  }
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Açık" : "Kapalı";
  // SelectOptions icin id → label cevirisi
  if (
    meta &&
    meta.type === "SelectOptions" &&
    Array.isArray(meta.options)
  ) {
    const found = meta.options.find((o) => {
      if (typeof o === "object") return (o.id ?? o.value) === value;
      return o === value;
    });
    if (found) {
      if (typeof found === "object") return found.label ?? found.value ?? value;
      // Sade string ise bilinen label varsa kullan
      return KNOWN_ID_LABELS[found] ?? String(found);
    }
  }
  // Setting metadata yoksa bile bilinen ID ise label kullan (defensive)
  if (typeof value === "string" && KNOWN_ID_LABELS[value]) {
    return KNOWN_ID_LABELS[value];
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "(boş)";
    if (value.every((v) => typeof v === "string" || typeof v === "number")) {
      return value.join(", ");
    }
    return `${value.length} öğe`;
  }
  if (typeof value === "object") {
    // Düz (primitif değerli) objeleri okunur "anahtar: değer" listesi yap —
    // ör. Türk Kahvesi Öğütücü Süreleri: "Küçük: 15, Büyük: 25".
    const entries = Object.entries(value);
    if (entries.length === 0) return "(boş)";
    if (
      entries.every(
        ([, v]) =>
          typeof v === "string" ||
          typeof v === "number" ||
          typeof v === "boolean",
      )
    ) {
      return entries
        .map(
          ([k, v]) =>
            `${k}: ${
              typeof v === "boolean" ? (v ? "Açık" : "Kapalı") : v
            }`,
        )
        .join(", ");
    }
    return `${entries.length} alan`;
  }
  return String(value);
};

// Cihaz cupSchema göndermeyen eski sürümler için bardak alanı etiketleri
const CUP_FIELD_LABELS = {
  name: "Ad",
  referenceTea: "Çay Referansı",
  referenceFilterCoffee: "Filtre Kahve Referansı",
  filledWeight: "Dolu Ağırlık (gr)",
  emptyWeight: "Boş Ağırlık (gr)",
  lowerTolerance: "Alt Tolerans (gr)",
  upperTolerance: "Üst Tolerans (gr)",
};

const formatPrimitive = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Açık" : "Kapalı";
  return String(v);
};

// Non-scalar (obje / obje-array) değerler için alan bazlı fark listesi.
// Bardak kayıtları gibi toptan-array gönderilen alanlarda "2 öğe → 2 öğe"
// chip'i ne değiştiğini göstermiyordu; bunun yerine öğe+alan bazında
// {label, from, to} satırları üretilir. Diff çıkarılamazsa null döner
// (çağıran chip fallback'ine düşer).
const buildDeepDiff = (oldVal, newVal, meta) => {
  if (Array.isArray(newVal) && newVal.some((v) => v && typeof v === "object")) {
    if (!Array.isArray(oldVal)) return null;
    const rows = [];
    const schema = meta?.cupSchema || {};
    const itemName = (item, idx) =>
      (item && (item.name || item.id)) || `${idx + 1}. öğe`;
    newVal.forEach((item, idx) => {
      const prev =
        item && item.id != null
          ? oldVal.find((o) => o && o.id === item.id)
          : oldVal[idx];
      if (!prev) {
        rows.push({ label: itemName(item, idx), from: "—", to: "eklendi" });
        return;
      }
      const keys = new Set([...Object.keys(prev), ...Object.keys(item || {})]);
      keys.forEach((k) => {
        if (k === "id" || k === "order") return;
        const a = prev[k];
        const b = item[k];
        if (typeof a === "object" || typeof b === "object") return;
        if (a !== b) {
          const fieldLabel = schema[k]?.label || CUP_FIELD_LABELS[k] || k;
          rows.push({
            label: `${itemName(item, idx)} · ${fieldLabel}`,
            from: formatPrimitive(a),
            to: formatPrimitive(b),
          });
        }
      });
    });
    oldVal.forEach((prev, idx) => {
      const still =
        prev && prev.id != null
          ? newVal.find((n) => n && n.id === prev.id)
          : newVal[idx];
      if (!still)
        rows.push({ label: itemName(prev, idx), from: "kayıtlı", to: "silindi" });
    });
    return rows;
  }
  if (newVal && typeof newVal === "object" && !Array.isArray(newVal)) {
    if (!oldVal || typeof oldVal !== "object" || Array.isArray(oldVal))
      return null;
    const rows = [];
    const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
    keys.forEach((k) => {
      const a = oldVal[k];
      const b = newVal[k];
      if (typeof a === "object" || typeof b === "object") return;
      if (a !== b)
        rows.push({ label: k, from: formatPrimitive(a), to: formatPrimitive(b) });
    });
    return rows;
  }
  return null;
};

// Eski→yeni gösterimi: non-scalar değerlerde alan bazlı diff satırları,
// scalar'da mevcut chip çifti.
const ValueDiff = ({ oldVal, newVal, meta, invalid }) => {
  const rows = buildDeepDiff(oldVal, newVal, meta);
  if (rows) {
    if (rows.length === 0) {
      return (
        <div className="text-xs text-gray-400 mt-1">
          İçerik güncellendi (alan farkı bulunamadı)
        </div>
      );
    }
    return (
      <div className="mt-1 space-y-1">
        {rows.map((r, i) => (
          <div
            key={i}
            className="text-xs font-mono flex items-center gap-1.5 flex-wrap"
          >
            <span className="text-gray-500">{r.label}:</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 line-through">
              {r.from}
            </span>
            <span className="text-gray-400">→</span>
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded font-semibold ${
                invalid ? "bg-fifth/10 text-fifth" : "bg-sixth/10 text-sixth"
              }`}
            >
              {r.to}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="text-sm font-mono mt-1 flex items-center gap-1.5 flex-wrap">
      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 line-through">
        {formatValue(oldVal, meta)}
      </span>
      <span className="text-gray-400">→</span>
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-md font-semibold ${
          invalid ? "bg-fifth/10 text-fifth" : "bg-sixth/10 text-sixth"
        }`}
      >
        {formatValue(newVal, meta)}
      </span>
    </div>
  );
};

export default function ChangesPanel({
  changes,
  onConfirm,
  onClear,
  onRemoveOne,
  isLoading,
  results,
  onClearResults,
  data,
  originalData,
  invalidChanges = [],
  isDrawer = false,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Panel arka plandaki ogeleri kapatmasin diye kucultulebilir — collapsed iken
  // sadece baslik bari gorunur, liste + footer gizlenir.
  const [collapsed, setCollapsed] = useState(false);
  if (changes.length === 0 && !results) return null;
  const rejected = results?.results?.filter((r) => !r.success) ?? [];
  // Bir change geçersiz mi? key bilesimine bakarak isaretleriz.
  const isChangeInvalid = (c) =>
    invalidChanges.some(
      (ic) =>
        ic.group === c.group &&
        ic.key === c.key &&
        ic.potIndex === c.potIndex &&
        ic.subgroup === c.subgroup &&
        ic.cupId === c.cupId &&
        ic.mode === c.mode,
    );
  const hasInvalid = invalidChanges.length > 0;

  // Konumlama: sayfa bağlamında mobil alt menü (Sidebar, z-50) fixed bottom-0
  // olduğundan panel hem onun ÜSTÜNDE (z) hem de yüksekliği + safe-area kadar
  // YUKARIDA durmalı. Drawer bağlamında ata eleman transform'lu olduğu için
  // fixed drawer'a göre çözülür ve alt menü drawer'ın altında kalır — ofset
  // gerekmez, panel drawer tabanına yapışır.
  const anchorClass = isDrawer
    ? "bottom-[calc(0.75rem+env(safe-area-inset-bottom,0px))] left-3 right-3 sm:bottom-6 sm:left-auto sm:right-6 sm:w-[26rem]"
    : "bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] left-3 right-3 md:bottom-6 md:left-auto md:right-6 md:w-[26rem]";
  const panelClass = `fixed ${anchorClass} z-[60] rounded-2xl border border-main/30 bg-white shadow-2xl w-auto max-w-full sm:max-w-[calc(100vw-3rem)] overflow-hidden`;

  return (
    <>
      {changes.length > 0 && !results && (
        <div className={panelClass}>
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-main text-white">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/15">
              <FaSave size={14} />
            </div>
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              className="flex-1 min-w-0 text-left"
              title={collapsed ? "Genişlet" : "Küçült"}
            >
              <p className="text-base font-bold leading-tight">
                Bekleyen Değişiklikler
              </p>
              <p className="text-xs text-white/70 leading-tight mt-0.5">
                {collapsed
                  ? `${changes.length} bekleyen — genişletmek için tıklayın`
                  : `${changes.length} alan cihaza gönderilmeyi bekliyor`}
              </p>
            </button>
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition"
              title={collapsed ? "Genişlet" : "Küçült"}
            >
              {collapsed ? <FaChevronUp size={13} /> : <FaChevronDown size={13} />}
            </button>
            <button
              type="button"
              onClick={onClear}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition"
              title="Tümünü temizle"
            >
              <FaTrash size={13} />
            </button>
          </div>

          {/* Change list */}
          {!collapsed && (
          <ul className="max-h-[40vh] sm:max-h-72 overflow-auto px-3 py-2 space-y-2 bg-background">
            {changes.map((c, i) => {
              const oldVal = resolveOldValue(originalData, c);
              const meta = resolveSettingMeta(data, c);
              const invalid = isChangeInvalid(c);
              return (
                <li
                  key={i}
                  className={`group flex items-start gap-2 rounded-lg bg-white border px-3 py-2 transition ${
                    invalid
                      ? "border-fifth/40 ring-1 ring-fifth/20"
                      : "border-gray-200 hover:border-main/40"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium leading-tight">
                      {buildChangeContext(c)}
                    </div>
                    <div className="text-sm font-semibold text-main truncate mt-0.5">
                      {resolveSettingLabel(data, c)}
                    </div>
                    <ValueDiff
                      oldVal={oldVal}
                      newVal={c.value}
                      meta={meta}
                      invalid={invalid}
                    />
                    {invalid && (
                      <div className="text-xs text-fifth font-medium mt-1.5 flex items-center gap-1">
                        <span>⚠</span>
                        <span>Geçersiz değer — düzeltin</span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveOne(c)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-300 hover:text-fifth hover:bg-fifth/10 transition shrink-0"
                    title="Geri al"
                  >
                    <FaTimes size={13} />
                  </button>
                </li>
              );
            })}
          </ul>
          )}

          {/* Footer */}
          {!collapsed && (
          <div className="px-4 py-3 border-t border-gray-200 bg-white">
            {hasInvalid && (
              <div className="text-sm text-fifth font-medium bg-fifth/10 border border-fifth/30 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
                <span className="text-base">⚠</span>
                <span>
                  {invalidChanges.length} geçersiz alan var — kaydetmek için düzeltin
                </span>
              </div>
            )}
            <button
              type="button"
              disabled={isLoading || hasInvalid}
              onClick={() => setConfirmOpen(true)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-main text-white px-4 py-2.5 text-base font-semibold hover:bg-mainSecond shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
              title={hasInvalid ? "Geçersiz alanları düzeltin" : ""}
            >
              <FaSave size={15} />
              {isLoading
                ? "Gönderiliyor..."
                : `Kaydet (${changes.length})`}
            </button>
          </div>
          )}
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
          <div className="rounded-2xl bg-white shadow-2xl w-[36rem] max-w-full max-h-[85vh] flex flex-col overflow-hidden">
            <div className="px-5 py-4 bg-main text-white flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/15">
                <FaSave size={15} />
              </div>
              <div>
                <h4 className="text-lg font-bold leading-tight">
                  Değişiklikleri Onayla
                </h4>
                <p className="text-sm text-white/75 mt-0.5">
                  {changes.length} alan cihaza gönderilecek
                </p>
              </div>
            </div>
            <ul className="max-h-80 min-h-0 flex-1 overflow-auto px-5 py-4 space-y-3 bg-background">
              {changes.map((c, i) => {
                const oldVal = resolveOldValue(originalData, c);
                const meta = resolveSettingMeta(data, c);
                return (
                  <li
                    key={i}
                    className="rounded-lg bg-white border border-gray-200 px-3 py-2.5"
                  >
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium leading-tight">
                      {buildChangeContext(c)}
                    </div>
                    <div className="text-sm font-semibold text-main mt-0.5">
                      {resolveSettingLabel(data, c)}
                    </div>
                    <ValueDiff oldVal={oldVal} newVal={c.value} meta={meta} />
                  </li>
                );
              })}
            </ul>
            <div className="px-5 py-3 border-t border-gray-200 flex gap-2 justify-end bg-white">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                İptal
              </button>
              <button
                type="button"
                disabled={isLoading || hasInvalid}
                onClick={async () => {
                  await onConfirm(changes);
                  setConfirmOpen(false);
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-main text-white px-4 py-2 text-base font-semibold hover:bg-mainSecond shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
                title={hasInvalid ? "Geçersiz alanları düzeltin" : ""}
              >
                <FaSave size={14} />
                Onayla ve Gönder
              </button>
            </div>
          </div>
        </div>
      )}

      {results && (
        <div className={panelClass}>
          <div className={`flex items-center gap-3 px-4 py-3 text-white ${
            rejected.length > 0 ? "bg-fifth" : "bg-sixth"
          }`}>
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/15 text-lg font-bold">
              {rejected.length > 0 ? "!" : "✓"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold leading-tight">
                {rejected.length > 0 ? "Bazı Alanlar Reddedildi" : "Tüm Alanlar Uygulandı"}
              </p>
              <p className="text-xs text-white/80 leading-tight mt-0.5">
                {results.summary?.accepted ?? 0} başarılı, {results.summary?.rejected ?? 0} reddedildi
              </p>
            </div>
            <button
              type="button"
              onClick={onClearResults}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition"
              title="Kapat"
            >
              <FaTimes size={14} />
            </button>
          </div>
          {rejected.length > 0 ? (
            <ul className="px-3 py-2 space-y-2 max-h-60 overflow-auto bg-background">
              {rejected.map((r, i) => (
                <li
                  key={i}
                  className="rounded-lg bg-white border border-fifth/30 px-3 py-2"
                >
                  <div className="text-sm font-semibold text-main">
                    {resolveSettingLabel(data, r)}
                  </div>
                  <div className="text-sm text-fifth mt-0.5">
                    {r.reason}
                    {r.expected ? ` (${r.expected})` : ""}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-sixth font-medium px-4 py-3 bg-white">
              Tüm alanlar başarıyla uygulandı.
            </p>
          )}
        </div>
      )}
    </>
  );
}
